import { TextInput, Select, MultiSelect } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useRef, useEffect, useState } from "react";
import {
  toCustomFieldInputValue,
  fromCustomFieldInputValue,
  optionData,
} from "../../utils/customFieldValues";

export const EditableCell = ({
  isExisting,
  value,
  onChange,
  error,
  inputProps = {},
  onSave,
  onCancel,
  type = "text",
  options = [],
  render,
  autoFocus,
}) => {
  const inputRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    // Only focus if we're in editing mode (not isExisting) and autoFocus is true
    if (!isExisting && autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus, isExisting]);

  if (isExisting) {
    // If the cell has a custom render function, use it
    if (render) {
      return render();
    }
    return value;
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent form submission
      if (!isSaving) {
        // Prevent multiple save events
        setIsSaving(true);
        onSave?.();
      }

      setTimeout(() => {
        setIsSaving(false);
      }, 300);
    } else if (e.key === "Escape") {
      onCancel?.();
    }
  };

  if (type === "select") {
    return (
      <Select
        ref={inputRef}
        value={toCustomFieldInputValue(value, "select", options)}
        onChange={onChange}
        data={optionData(options)}
        error={error}
        onKeyDown={handleKeyDown}
        {...inputProps}
      />
    );
  }

  if (type === "tags") {
    return (
      <MultiSelect
        ref={inputRef}
        defaultValue={value?.map((tag) => tag.label) || []}
        onChange={(selectedLabels) => {
          const selectedTags = options.filter(
            (option) =>
              selectedLabels.includes(option.label) ||
              selectedLabels.includes(option.value)
          );

          onChange(selectedTags.map((tag) => tag.value));
        }}
        data={options}
        error={error}
        onKeyDown={handleKeyDown}
        comboboxProps={{ width: 150, position: "bottom-start" }}
        checkIconPosition="start"
        {...inputProps}
      />
    );
  }

  // Custom field values travel as strings, so each typed control parses on the
  // way in and serializes on the way out.
  if (type === "multiselect") {
    return (
      <MultiSelect
        ref={inputRef}
        value={toCustomFieldInputValue(value, "multiselect", options)}
        onChange={(selected) =>
          onChange(fromCustomFieldInputValue(selected, "multiselect"))
        }
        data={optionData(options)}
        error={error}
        onKeyDown={handleKeyDown}
        comboboxProps={{ width: 200, position: "bottom-start" }}
        {...inputProps}
      />
    );
  }

  if (type === "date") {
    return (
      <DatePickerInput
        ref={inputRef}
        value={toCustomFieldInputValue(value, "date")}
        onChange={(date) => onChange(fromCustomFieldInputValue(date, "date"))}
        valueFormat="DD.MM.YYYY."
        clearable
        error={error}
        {...inputProps}
      />
    );
  }

  return (
    <TextInput
      ref={inputRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={error}
      onKeyDown={handleKeyDown}
      {...inputProps}
    />
  );
};
